import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Send, Loader2, Camera, FolderOpen, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import FormularioConfirmacion from '../components/FormularioConfirmacion';
import { logErrorToBackend } from '../utils/errorLogger';
import { parseApiError } from '../utils/parseApiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Formulario1() {
  const navigate = useNavigate();
  const [paso, setPaso] = useState(1); // 1: Verificación de cédula, 2: Formulario
  const [verificando, setVerificando] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showConfirmacion, setShowConfirmacion] = useState(false);
  const [formData, setFormData] = useState({
    nombre_completo: '',
    cedula: '',
    email: '',
    telefono: '',
    direccion: '',
    motivo_retiro: ''
  });
  const [documentos, setDocumentos] = useState({
    cedula_doc: null,
    rif_doc: null
  });

  // Helper: XMLHttpRequest con timeout y reintento
  const xhrRequest = (method, url, options = {}) => {
    const { body, headers = {}, timeout = 30000, retries = 1 } = options;
    
    const attempt = (retriesLeft) => new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.timeout = timeout;
      if (options.withCredentials) xhr.withCredentials = true;
      
      Object.entries(headers).forEach(([key, val]) => xhr.setRequestHeader(key, val));
      
      xhr.onload = function() {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        } catch (e) {
          reject(new Error('Respuesta inválida del servidor'));
        }
      };
      
      xhr.onerror = function() {
        if (retriesLeft > 0) {
          setTimeout(() => attempt(retriesLeft - 1).then(resolve).catch(reject), 2000);
        } else {
          reject(new Error('Error de conexión. Verifica tu internet e intenta nuevamente.'));
        }
      };
      
      xhr.ontimeout = function() {
        if (retriesLeft > 0) {
          setTimeout(() => attempt(retriesLeft - 1).then(resolve).catch(reject), 2000);
        } else {
          reject(new Error('La conexión tardó demasiado. Verifica tu internet e intenta nuevamente.'));
        }
      };
      
      xhr.send(body || null);
    });
    
    return attempt(retries);
  };

  // Formato de cédula: 11.111.111
  const formatCedula = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}`;
  };

  // Formato de teléfono: 0111-1111111
  const formatTelefono = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 4) return numbers;
    return `${numbers.slice(0, 4)}-${numbers.slice(4, 11)}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'cedula') {
      formattedValue = formatCedula(value);
    } else if (name === 'telefono') {
      formattedValue = formatTelefono(value);
    }

    setFormData({
      ...formData,
      [name]: formattedValue
    });
  };

  // Verificar si ya existe un expediente con esta cédula
  const verificarCedula = async (e) => {
    e.preventDefault();
    
    if (!formData.cedula || !formData.email) {
      toast.error('Por favor ingresa tu cédula y email');
      return;
    }

    setVerificando(true);

    try {
      const result = await xhrRequest('POST', `${BACKEND_URL}/api/buscar-expediente`, {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cedula: formData.cedula.replace(/\D/g, ''),
          email: formData.email.toLowerCase().trim()
        }),
        timeout: 15000,
        retries: 1
      });

      if (result.ok) {
        // Expediente encontrado - mostrar error
        toast.error(`Ya existe un expediente activo con esta cédula: ${result.data.expediente.numero_expediente}. Por favor contacta a soporte si necesitas ayuda.`);
      } else if (result.status === 404) {
        // No existe expediente - puede continuar
        setPaso(2);
        toast.success('Cédula verificada. Por favor completa el formulario.');
      } else if (result.status === 403) {
        // Email no coincide
        toast.error(parseApiError(result.data?.detail, 'El email no coincide con el registrado.'));
      } else {
        // Otro error del servidor
        toast.error(parseApiError(result.data?.detail, 'Error al verificar. Intenta nuevamente.'));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error de conexión. Verifica tu internet e intenta nuevamente.');
    } finally {
      setVerificando(false);
    }
  };

  const handleFileUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño (máximo 10MB)
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_SIZE) {
      toast.error(`El archivo es demasiado grande. Máximo 10MB permitido. Tamaño actual: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      e.target.value = '';
      return;
    }

    // Validar tipo de archivo - Más flexible para dispositivos móviles
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf', 'image/heic', 'image/heif'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.pdf', '.heic', '.heif'];
    
    // Verificar por tipo MIME o por extensión
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
    
    // En móviles, image/* puede venir sin MIME específico, así que ser más permisivo
    const isMobileImage = file.type.startsWith('image/');
    
    if (!isValidType && !isMobileImage) {
      toast.error('Solo se permiten imágenes (JPG, PNG, HEIC) o archivos PDF');
      e.target.value = '';
      return;
    }

    setUploadingFile(docType);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const result = await xhrRequest('POST', `${BACKEND_URL}/api/upload/documento`, {
        body: formDataUpload,
        withCredentials: true,
        timeout: 60000,  // 60s para uploads
        retries: 2       // 2 reintentos para uploads
      });

      if (!result.ok) {
        throw new Error(parseApiError(result.data?.detail, 'Error al subir archivo'));
      }

      // Usar nombres normalizados en lugar del nombre del archivo original
      const nombreNormalizado = docType === 'cedula_doc' ? 'Cédula de Identidad' : 'RIF';
      
      setDocumentos(prev => ({
        ...prev,
        [docType]: {
          nombre: nombreNormalizado,
          url: result.data.url,
          cloudinary_id: result.data.cloudinary_id,
          uploaded_at: new Date().toISOString()
        }
      }));
      toast.success(`${docType === 'cedula_doc' ? 'Cédula' : 'RIF'} subido exitosamente`);
      e.target.value = '';
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error de conexión al subir documento. Verifica tu internet e intenta nuevamente.');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre_completo || !formData.cedula || !formData.email) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    if (!documentos.cedula_doc || !documentos.rif_doc) {
      toast.error('Por favor sube tanto la Cédula como el RIF');
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append('nombre_completo', formData.nombre_completo);
      params.append('cedula', formData.cedula);
      params.append('email', formData.email);
      params.append('telefono', formData.telefono);
      params.append('direccion', formData.direccion);
      params.append('motivo_retiro', formData.motivo_retiro);
      
      // Convertir documentos a array para el backend
      const docsArray = [documentos.cedula_doc, documentos.rif_doc];
      params.append('documentos', JSON.stringify(docsArray));

      const result = await xhrRequest('POST', `${BACKEND_URL}/api/expedientes/crear`, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
        withCredentials: true,
        timeout: 30000,
        retries: 1
      });

      if (result.ok) {
        // Mostrar pantalla de confirmación
        setShowConfirmacion(true);
      } else {
        // Manejar errores del backend
        const errorMsg = parseApiError(result.data?.detail, `Error al enviar solicitud (${result.status})`);
        toast.error(errorMsg);
        logErrorToBackend({ tipo: 'formulario1_error', endpoint: '/api/expedientes/crear', error: errorMsg, usuario: { cedula: formData.cedula, email: formData.email } });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error de conexion al enviar solicitud. Verifique su internet e intente nuevamente.');
      logErrorToBackend({ tipo: 'formulario1_conexion_error', endpoint: '/api/expedientes/crear', error: error.message, usuario: { cedula: formData.cedula, email: formData.email } });
    } finally {
      setLoading(false);
    }
  };

  const removeDocumento = (docType) => {
    setDocumentos(prev => ({
      ...prev,
      [docType]: null
    }));
  };

  // Paso 1: Verificación de cédula
  if (paso === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center py-12 px-6">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Solicitud de Retiro</h1>
            <p className="text-gray-400">Verificación de identidad</p>
          </div>

          <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Verificar Cédula</CardTitle>
              <CardDescription className="text-gray-400">
                Primero verificaremos si ya tienes un expediente activo
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={verificarCedula} className="space-y-4">
                <div>
                  <Label className="text-gray-300">Cédula *</Label>
                  <Input
                    name="cedula"
                    value={formData.cedula}
                    onChange={handleInputChange}
                    placeholder="12.345.678"
                    maxLength={11}
                    inputMode="numeric"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    data-testid="form1-cedula-input"
                    className="bg-gray-700 border-gray-600 text-white mt-2"
                    required
                  />
                </div>

                <div>
                  <Label className="text-gray-300">Email *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="tu@email.com"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    data-testid="form1-email-input"
                    className="bg-gray-700 border-gray-600 text-white mt-2"
                    required
                  />
                </div>

                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-blue-300 text-sm">
                      Si ya tienes un expediente activo, no podrás crear uno nuevo. Por favor contacta a soporte.
                    </p>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={verificando}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                >
                  {verificando ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    'Verificar y Continuar'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="text-center text-gray-500 text-sm mt-6">
            ¿Necesitas ayuda? Escríbenos a <a href="mailto:desactivate@kompa.global" className="text-yellow-500 hover:text-yellow-400">desactivate@kompa.global</a>
          </p>
        </div>
      </div>
    );
  }

  // Paso 2: Formulario completo
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black py-12 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/kompa-logo.png" alt="Kompa" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Solicitud de Retiro</h1>
          <p className="text-gray-400">Formulario de información inicial</p>
        </div>

        {/* Botón para volver */}
        <Button
          variant="ghost"
          onClick={() => setPaso(1)}
          className="mb-4 text-gray-400 hover:text-white"
        >
          ← Volver a verificación
        </Button>

        {/* Formulario */}
        <Card className="bg-gray-800/50 backdrop-blur-xl border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Datos Personales</CardTitle>
            <CardDescription className="text-gray-400">
              Ingresa tu información de contacto
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Datos Personales */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Nombre Completo *</Label>
                  <Input
                    name="nombre_completo"
                    value={formData.nombre_completo}
                    onChange={handleInputChange}
                    className="bg-gray-700 border-gray-600 text-white mt-2"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Cédula *</Label>
                  <Input
                    name="cedula"
                    value={formData.cedula}
                    onChange={handleInputChange}
                    className="bg-gray-700 border-gray-600 text-white mt-2"
                    disabled
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Email *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="bg-gray-700 border-gray-600 text-white mt-2"
                    disabled
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Teléfono</Label>
                  <Input
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    placeholder="0412-1234567"
                    className="bg-gray-700 border-gray-600 text-white mt-2"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Dirección</Label>
                <Textarea
                  name="direccion"
                  value={formData.direccion}
                  onChange={handleInputChange}
                  placeholder="Dirección completa..."
                  className="bg-gray-700 border-gray-600 text-white mt-2"
                  rows={3}
                />
              </div>

              <div>
                <Label className="text-gray-300">Motivo del Retiro (Opcional)</Label>
                <Textarea
                  name="motivo_retiro"
                  value={formData.motivo_retiro}
                  onChange={handleInputChange}
                  placeholder="Describe brevemente por qué solicitas el retiro..."
                  className="bg-gray-700 border-gray-600 text-white mt-2"
                  rows={3}
                />
              </div>

              {/* Documentos */}
              <div className="border-t border-gray-700 pt-6">
                <h3 className="text-white font-semibold mb-4">Documentos Requeridos</h3>
                
                {/* Cédula */}
                <div className="mb-4">
                  <Label className="text-gray-300 mb-2 block">Cédula de Identidad *</Label>
                  {!documentos.cedula_doc ? (
                    <div className="space-y-3">
                      {/* Inputs ocultos */}
                      <input
                        type="file"
                        id="cedula-camera"
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleFileUpload(e, 'cedula_doc')}
                        disabled={uploadingFile === 'cedula_doc'}
                      />
                      <input
                        type="file"
                        id="cedula-gallery"
                        className="hidden"
                        accept="image/*,.pdf,.heic,.heif"
                        onChange={(e) => handleFileUpload(e, 'cedula_doc')}
                        disabled={uploadingFile === 'cedula_doc'}
                      />
                      
                      {/* Botones de selección */}
                      <div className="grid grid-cols-2 gap-3">
                        <label 
                          htmlFor="cedula-camera" 
                          className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="flex flex-col items-center gap-2">
                            {uploadingFile === 'cedula_doc' ? (
                              <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
                            ) : (
                              <Camera className="w-8 h-8 text-yellow-500" />
                            )}
                            <p className="text-gray-300 font-medium text-sm">
                              {uploadingFile === 'cedula_doc' ? 'Subiendo...' : 'Tomar Foto'}
                            </p>
                            <p className="text-xs text-gray-500">Usar cámara</p>
                          </div>
                        </label>
                        
                        <label 
                          htmlFor="cedula-gallery" 
                          className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="flex flex-col items-center gap-2">
                            {uploadingFile === 'cedula_doc' ? (
                              <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
                            ) : (
                              <FolderOpen className="w-8 h-8 text-blue-500" />
                            )}
                            <p className="text-gray-300 font-medium text-sm">
                              {uploadingFile === 'cedula_doc' ? 'Subiendo...' : 'Elegir Archivo'}
                            </p>
                            <p className="text-xs text-gray-500">Galería/PDF</p>
                          </div>
                        </label>
                      </div>
                      <p className="text-xs text-center text-gray-500">PDF, JPG, PNG, HEIC - Máx 10MB</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-green-500" />
                        <div>
                          <p className="text-gray-300 font-medium">{documentos.cedula_doc.nombre}</p>
                          <p className="text-xs text-green-400">✓ Cédula cargada</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDocumento('cedula_doc')}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        Eliminar
                      </Button>
                    </div>
                  )}
                </div>

                {/* RIF */}
                <div>
                  <Label className="text-gray-300 mb-2 block">RIF (Registro de Información Fiscal) *</Label>
                  {!documentos.rif_doc ? (
                    <div className="space-y-3">
                      {/* Inputs ocultos */}
                      <input
                        type="file"
                        id="rif-camera"
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => handleFileUpload(e, 'rif_doc')}
                        disabled={uploadingFile === 'rif_doc'}
                      />
                      <input
                        type="file"
                        id="rif-gallery"
                        className="hidden"
                        accept="image/*,.pdf,.heic,.heif"
                        onChange={(e) => handleFileUpload(e, 'rif_doc')}
                        disabled={uploadingFile === 'rif_doc'}
                      />
                      
                      {/* Botones de selección */}
                      <div className="grid grid-cols-2 gap-3">
                        <label 
                          htmlFor="rif-camera" 
                          className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="flex flex-col items-center gap-2">
                            {uploadingFile === 'rif_doc' ? (
                              <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
                            ) : (
                              <Camera className="w-8 h-8 text-yellow-500" />
                            )}
                            <p className="text-gray-300 font-medium text-sm">
                              {uploadingFile === 'rif_doc' ? 'Subiendo...' : 'Tomar Foto'}
                            </p>
                            <p className="text-xs text-gray-500">Usar cámara</p>
                          </div>
                        </label>
                        
                        <label 
                          htmlFor="rif-gallery" 
                          className="cursor-pointer border-2 border-dashed border-gray-700 rounded-xl p-4 text-center hover:border-yellow-500/50 hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="flex flex-col items-center gap-2">
                            {uploadingFile === 'rif_doc' ? (
                              <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
                            ) : (
                              <FolderOpen className="w-8 h-8 text-blue-500" />
                            )}
                            <p className="text-gray-300 font-medium text-sm">
                              {uploadingFile === 'rif_doc' ? 'Subiendo...' : 'Elegir Archivo'}
                            </p>
                            <p className="text-xs text-gray-500">Galería/PDF</p>
                          </div>
                        </label>
                      </div>
                      <p className="text-xs text-center text-gray-500">PDF, JPG, PNG, HEIC - Máx 10MB</p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-green-500" />
                        <div>
                          <p className="text-gray-300 font-medium">{documentos.rif_doc.nombre}</p>
                          <p className="text-xs text-green-400">✓ RIF cargado</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDocumento('rif_doc')}
                        className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                      >
                        Eliminar
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold text-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Enviar Solicitud
                  </>
                )}
              </Button>

              <p className="text-center text-sm text-gray-500">
                * Campos requeridos
              </p>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Modal de confirmación */}
      {showConfirmacion && (
        <FormularioConfirmacion 
          onClose={() => {
            setShowConfirmacion(false);
            window.close(); // Intentar cerrar la ventana/tab
          }}
        />
      )}
    </div>
  );
}
